import { KV, KVRow } from '../components/KV';
import type { AppRenderer, AppPayload } from './types';

interface LinkedInData {
  linkedin_author_urn?: string;
  access_token_expiry?: string;
  refresh_token_expiry?: string;
  last_post_at?: string | null;
  alert_30d_sent?: boolean;
  alert_7d_sent?: boolean;
  updated_at?: string;
}

interface WriterProfile {
  name?: string;
  role?: string;
  company?: string;
  industry?: string;
  target_audience?: string;
  writing_style?: string;
  post_examples?: string[];
  last_synced?: string;
  updated_at?: string;
}

function days_until(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function token_status_label(expiry_iso: string | null | undefined): {
  text: string;
  color: string;
} {
  const days = days_until(expiry_iso);
  if (days === null) return { text: 'unknown', color: 'var(--muted)' };
  if (days < 0)
    return { text: `expired ${-days}d ago`, color: '#b3261e' };
  if (days < 7) return { text: `linked, expires in ${days}d`, color: '#b58900' };
  return { text: `linked, expires in ${days}d`, color: 'var(--green-700)' };
}

function format_dt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

export const LinkedInRenderer: AppRenderer = {
  app_name: 'linkedin',
  render(payload: AppPayload) {
    const d = payload.data as LinkedInData;
    const profile_doc = payload.subcollections?.writer_profile?.find(
      (doc) => doc.id === 'v1'
    );
    const profile = profile_doc?.data as WriterProfile | undefined;

    const access = token_status_label(d.access_token_expiry);
    const refresh = token_status_label(d.refresh_token_expiry);

    return (
      <div>
        <h3 style={{ marginTop: 0 }}>Token status</h3>
        <KV>
          <KVRow
            label="Access token"
            value={<strong style={{ color: access.color }}>{access.text}</strong>}
          />
          <KVRow
            label="Refresh token"
            value={<strong style={{ color: refresh.color }}>{refresh.text}</strong>}
          />
        </KV>

        <h3 style={{ marginTop: 24 }}>LinkedIn account</h3>
        <KV>
          <KVRow
            label="Author URN"
            value={<code>{d.linkedin_author_urn ?? '—'}</code>}
          />
          <KVRow label="Last post" value={format_dt(d.last_post_at)} />
          <KVRow label="30d alert sent" value={String(d.alert_30d_sent ?? false)} />
          <KVRow label="7d alert sent" value={String(d.alert_7d_sent ?? false)} />
          <KVRow label="Updated" value={format_dt(d.updated_at)} />
        </KV>

        {profile ? (
          <>
            <h3 style={{ marginTop: 24 }}>Writer profile</h3>
            <KV>
              <KVRow label="Name" value={profile.name ?? '—'} />
              <KVRow label="Role" value={profile.role ?? '—'} />
              <KVRow label="Company" value={profile.company ?? '—'} />
              <KVRow label="Industry" value={profile.industry ?? '—'} />
              <KVRow
                label="Target audience"
                value={profile.target_audience ?? '—'}
              />
              <KVRow
                label="Writing style"
                value={
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
                    {profile.writing_style ?? '—'}
                  </pre>
                }
              />
              <KVRow label="Last synced" value={format_dt(profile.last_synced)} />
              <KVRow label="Updated" value={format_dt(profile.updated_at)} />
            </KV>
            {profile.post_examples?.length ? (
              <>
                <h4 style={{ margin: '20px 0 8px' }}>
                  Post examples ({profile.post_examples.length})
                </h4>
                {profile.post_examples.map((ex, i) => (
                  <pre
                    key={i}
                    style={{
                      background: 'var(--green-50)',
                      padding: 12,
                      borderRadius: 6,
                      whiteSpace: 'pre-wrap',
                      fontSize: 13,
                      fontFamily: 'inherit',
                      marginBottom: 8,
                    }}
                  >
                    {ex}
                  </pre>
                ))}
              </>
            ) : null}
          </>
        ) : (
          <p className="muted" style={{ marginTop: 24 }}>
            No writer profile generated yet.
          </p>
        )}
      </div>
    );
  },
};
